<script setup lang="ts">
import { computed, onMounted, onUnmounted, watch } from 'vue'
import { CONTACT_FORM_URL } from '@/constants/app'

const currentYear = computed(() => new Date().getFullYear())

const props = defineProps<{ open: boolean }>()
const emit = defineEmits<{ 'update:open': [value: boolean] }>()

function close() {
  emit('update:open', false)
}

function onKeyDown(e: KeyboardEvent) {
  if (props.open && e.key === 'Escape') close()
}

onMounted(() => window.addEventListener('keydown', onKeyDown))
onUnmounted(() => window.removeEventListener('keydown', onKeyDown))

watch(() => props.open, open => {
  document.body.style.overflow = open ? 'hidden' : ''
})
</script>

<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="open"
        class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
        @click.self="close"
      >
        <div class="bg-surface rounded-xl shadow-2xl max-w-5xl w-full max-h-[85vh] flex flex-col">
          <div class="flex items-center justify-between px-5 py-3 border-b border-border shrink-0">
            <h2 class="text-lg font-bold text-text">サイトについて</h2>
            <button
              class="text-text-faint hover:text-text-tertiary"
              aria-label="閉じる"
              @click="close"
            >
              <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M18 6 6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div class="flex-1 overflow-y-auto px-6 py-5 divide-y divide-border text-sm text-text-secondary leading-relaxed">
            <section class="py-4 first:pt-0 last:pb-0">
              <p>
                本サイトは、デュエル・マスターズ プレイスのカード情報を閲覧・検索できる非公式のファンサイトです。
              </p>
              <p class="mt-2">
                本サイトの開発には、AIによるプログラミング支援を利用しています。
              </p>
              <p class="mt-2">
                運営はファン有志によるもので、株式会社タカラトミー、Wizards of the Coast LLC. その他関連企業とは一切関係ありません。
              </p>
            </section>

            <section class="py-4 first:pt-0 last:pb-0">
              <h3 class="text-base font-bold text-text mb-2 pl-2 border-l-2 border-blue-400">著作権について</h3>
              <p>
                本サイトに掲載されているカード画像・カードテキスト・ゲーム内の各種名称等の著作権および商標権は、株式会社タカラトミーおよび Wizards of the Coast LLC. に帰属します。
              </p>
              <p class="mt-2 text-text-muted">
                &copy;{{ currentYear }} Wizards of the Coast/Shogakukan/WHC<br />
                &copy;ＴＯＭＹ
              </p>
            </section>

            <section class="py-4 first:pt-0 last:pb-0">
              <h3 class="text-base font-bold text-text mb-2 pl-2 border-l-2 border-blue-400">リンク・データの利用について</h3>
              <p>
                本サイトへのリンクは自由です。
              </p>
              <p class="mt-2">
                一方、本サイトが提供するデータ・画像・テキスト等を取得・複製・転載して他の用途に利用する行為（スクレイピング、他サイト・他サービスでの再利用、アプリやツールへの取り込み等を含む）はご遠慮ください。
              </p>
            </section>

            <section class="py-4 first:pt-0 last:pb-0">
              <h3 class="text-base font-bold text-text mb-2 pl-2 border-l-2 border-blue-400">免責事項</h3>
              <p>
                本サイトは個人が運営する非公式のファンサイトであり、掲載情報の正確性・完全性・最新性について保証するものではありません。
              </p>
              <p class="mt-2">
                本サイトの利用により生じたいかなる損害についても、運営者は一切の責任を負いません。あらかじめご了承ください。
              </p>
            </section>

            <section class="py-4 first:pt-0 last:pb-0">
              <h3 class="text-base font-bold text-text mb-2 pl-2 border-l-2 border-blue-400">お問い合わせ</h3>
              <p>
                不具合報告・カード情報の誤り・機能の要望、および権利者の方からのご連絡は、下記のお問い合わせフォームよりお気軽にお寄せください。
              </p>
              <p class="mt-2">
                権利者の方は、フォーム内「お問い合わせの種類」にて「<span class="text-text font-semibold">権利者から</span>」をご選択ください。
              </p>
              <p class="mt-3">
                <a
                  :href="CONTACT_FORM_URL"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex items-center gap-1.5 text-blue-400 hover:text-blue-300"
                >
                  お問い合わせフォームを開く
                  <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M7 17 17 7M7 7h10v10"/>
                  </svg>
                </a>
              </p>
            </section>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity 0.15s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>
